import React, { useState, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useAppSelector } from '../../../state/store';
import { getLocationsById, getSensorsById } from '../../../state/selectors';
import { sensorTypeDetails } from '../../../utils/sensorProperties';
import SensorIcon from '../../../styles/icons/SensorIcon';
import { themeProps } from '../../../styles/theme';
import useGlobalStyles from '../../../styles';
import useStyles from '../../../styles/tags';

interface TaggedSensorsListProps {
  taggedSensors: string[];
  existingTaggedSensors: string[];
  handleTagging: (value: string) => void;
}

function TaggedSensorsList({
  taggedSensors,
  existingTaggedSensors,
  handleTagging,
}: TaggedSensorsListProps) {
  const { classes: globalClasses } = useGlobalStyles();
  const { classes } = useStyles();
  const sensorsById = useAppSelector(getSensorsById);
  const locationsById = useAppSelector(getLocationsById);
  const [hoveredId, setHoveredId] = useState('');

  // combine all old grouped and new grouped sensors without repeating
  const allSensors = useMemo(
    () => [
      ...existingTaggedSensors,
      ...taggedSensors.filter((item) => !existingTaggedSensors.includes(item)),
    ],
    [existingTaggedSensors, taggedSensors]
  );

  const sortedSensors = useMemo(
    () =>
      [...allSensors].sort((a, b) => {
        const nameA = sensorsById.get(a)?.name || a;
        const nameB = sensorsById.get(b)?.name || b;
        return nameA.localeCompare(nameB);
      }),
    [allSensors, sensorsById]
  );

  const getSensorChangeState = (sensorId: string): 'removed' | 'added' | 'unchanged' => {
    const wasExisting = existingTaggedSensors.includes(sensorId);
    const isCurrentlyTagged = taggedSensors.includes(sensorId);
    if (wasExisting && !isCurrentlyTagged) return 'removed';
    if (!wasExisting && isCurrentlyTagged) return 'added';
    return 'unchanged';
  };

  const getColor = (changeState: ReturnType<typeof getSensorChangeState>) => {
    if (changeState === 'removed') return themeProps.colors.warningRed;
    if (changeState === 'added') return themeProps.colors.happyGreen;
    return themeProps.colors.cobalt;
  };

  const getPillLabel = (changeState: ReturnType<typeof getSensorChangeState>) =>
    changeState === 'removed' ? 'Click to add' : 'Click to remove';

  return (
    <Box sx={{ marginTop: '16px' }}>
      <Typography variant="h6">Tagged Sensors</Typography>
      <List>
        {sortedSensors.map((sensorId) => {
          const sensorDetail = sensorsById.get(sensorId);
          const Icon = sensorDetail?.type
            ? sensorTypeDetails[sensorDetail.type]?.icon ?? SensorIcon
            : SensorIcon;
          const changeState = getSensorChangeState(sensorId);
          const color = getColor(changeState);

          return (
            <ListItem
              key={sensorId}
              className={globalClasses.compareListItem}
              sx={{ background: `${color}60 !important` }}
              onClick={() => handleTagging(sensorId)}
              onMouseEnter={() => setHoveredId(sensorId)}
              onMouseLeave={() => setHoveredId('')}
            >
              <Grid container sx={{ alignItems: 'center' }}>
                <Grid size={{ sm: 5 }} sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <Icon sx={{ color }} />
                  <Typography variant="body1" style={{ marginLeft: '5px' }}>
                    {sensorDetail?.name ?? sensorId}
                  </Typography>
                </Grid>
                <Grid size={{ sm: 3 }}>
                  {locationsById.get(sensorDetail?.location ?? '')?.name}
                </Grid>
                <Grid size={{ sm: 4 }}>
                  {hoveredId === sensorId && (
                    <span className={classes.tagPill}>{getPillLabel(changeState)}</span>
                  )}
                </Grid>
              </Grid>
            </ListItem>
          );
        })}
      </List>
      {sortedSensors.length === 0 && (
        <Alert severity="error">No sensors Selected for grouping</Alert>
      )}
    </Box>
  );
}
export default TaggedSensorsList;
